import { useCallback, useEffect, useReducer, useState } from 'react';
import { arrayFilter, arrayPages } from '../../../../../../common/filtering';
import { FetchRefreshFunctionParams } from '../../../../../../common/useFetch';
import useListFetch from '../../../../../../common/useListFetch';
import CmonProxysqlService from '../../../../../../services/cmon/CmonProxysqlService';
import {
    initialListState,
    listReducer,
    setListAll,
} from '../../../../../../common/listReducer';
import { getSortAlphabeticFn } from '../../../../../../common/sorting';

type ListParams = {
    page?: number;
    pageSize?: number;
    order?: (a: any, b: any) => any;
    filters?: Function[];
};

type FilterFunctionParams = ListParams & { arr?: any[] };
type RefreshFunctionParams = FetchRefreshFunctionParams;

export type UseProxySQLUsersListProps = {
    pageSize?: number;
    clusterId: number | undefined;
    hostName: string | undefined;
    filters?: Function[];
};
export default function useProxySQLUsers({
    pageSize = 15,
    clusterId,
    filters,
    hostName,
}: UseProxySQLUsersListProps) {
    const {
        loading,
        loaded,
        list: usersList,
        refresh: refreshUsers,
    } = useListFetch({
        name: 'queryUsers',
        pageSize,
        fetchFn: async ({ pageSize, page, ...rest }, opts) => {
            const response = await CmonProxysqlService.queryusers(
                {
                    cluster_id: clusterId,
                    hostName: hostName,
                    ...rest,
                },
                opts
            );

            return {
                list: response?.queryResults,
                total: response?.queryResultTotalCount,
            };
        },
        cancelFn: async ({ requestId }) => {
            await CmonProxysqlService.cancelRequest(requestId);
        },
    });

    const [list, setList] = useState<any[]>();
    const [total, setTotal] = useState<number>(0);

    const [
        {
            page: listPage,
            pageSize: listPageSize,
            order: listOrder,
            filters: listFilters,
        },
        listDispatch,
    ] = useReducer(listReducer, {
        ...initialListState,
        pageSize: pageSize || 0,
        filters: filters,
        order: getSortAlphabeticFn('ascend', (x) => x.username),
    });

    const filter = useCallback<(p?: FilterFunctionParams) => void>(
        ({
            page = listPage,
            pageSize = listPageSize,
            order = listOrder,
            filters = listFilters,
            arr = usersList || [],
        } = {}) => {
            listDispatch(setListAll({ page, pageSize, order, filters }));
            const filteredArr = arrayFilter({
                filters: [...(filters || [])],
                arr,
            });

            setList([
                ...arrayPages({
                    page,
                    pageSize,
                    order,
                    arr: filteredArr,
                }),
            ]);
            setTotal(filteredArr.length);
        },

        [listPage, listPageSize, listOrder, listFilters, usersList]
    );
    const refresh = useCallback<(p?: RefreshFunctionParams) => Promise<void>>(
        async ({
            page = listPage,
            pageSize = listPageSize,
            order = listOrder,
            filters = listFilters,
            ...rest
        } = {}) => {
            listDispatch(setListAll({ page, pageSize, order, filters }));
            await refreshUsers({
                ...rest,
            });
        },
        []
    );

    useEffect(() => {
        if (Array.isArray(usersList)) {
            filter({
                arr: usersList,
            });
        }
    }, [usersList]);

    return {
        list,
        loading,
        loaded,
        refresh,
        filter,
        page: listPage,
        pageSize: listPageSize,
        total,
    };
}
